import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import externalLinkIcon from 'core/assets/svg/external-link.svg';

import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import CollapsibleCard from 'core/components/collapsible-card';
import { Tooltip } from 'core/components/tooltip';
import helpCircleIcon from 'core/assets/svg/help-circle.svg';
import { dateFormatter } from 'core/helpers/formatters';
import { track } from 'core/helpers/analytics';
import { useToast } from 'core/components/toast';
import { homeClassForAutoClaimClass } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';
import { IgnoreDialogMode, IgnoreIncidentsDialog, countLabel } from 'common/components/ignore-incidents';
import {
  buildAutoIncidentRows,
  getHomeCopiesToIgnore,
  getIgnoredIncidentsAfterIgnoring,
  getIgnoredIncidentsAfterStopping,
  getOtherIncidentsOnSameDates
} from 'common/helpers/ignored-incidents';
import PersonIncidents from './person-incidents';
import { useOfferIncidentIgnores } from './use-offer-incident-ignores';
import useStyles from './factors-impacting-price.styles';

const itemCountLabel = (count) => (count === 1 ? '1 item' : `${count} items`);

const COURT_RECORD_TOOLTIP =
  "Branch may identify violations from court or public records that other carriers don't display.";

const FactorItem = ({ factor, classes }) => (
  <div>
    <div className={classes.factorTitleRow}>
      <span className={factor.date ? classes.factorViolationTitle : classes.factorPropertyTitle}>{factor.title}</span>
      {factor.moreInfo && (
        <Tooltip text={factor.moreInfoTooltip} placement="top" smallIcon={{ src: helpCircleIcon }} iconSize="icon16" />
      )}
    </div>
    {factor.description && <p className={classes.factorDescription}>{factor.description}</p>}
    {factor.date && (
      <div className={classes.factorMetadata}>
        <span>{dateFormatter(factor.date)}</span>
        {factor.source && (
          <>
            <span>&bull;</span>
            <span>Source: {factor.source}</span>
            {factor.isCourtRecord && (
              <Tooltip
                text={COURT_RECORD_TOOLTIP}
                placement="top"
                smallIcon={{ src: helpCircleIcon }}
                iconSize="icon16"
              />
            )}
          </>
        )}
      </div>
    )}
  </div>
);

const SAVE_ERROR = "We couldn't update the price. Try again.";

const savedMessage = (mode, count) =>
  mode === IgnoreDialogMode.Ignore
    ? countLabel(count, 'Price updated. 1 incident ignored.', 'Price updated. {n} incidents ignored.')
    : countLabel(count, 'Price updated. 1 incident counts again.', 'Price updated. {n} incidents count again.');

const incidentKey = ({ date, class: incidentClass }) => `${date}|${incidentClass}`;

const FactorsImpactingPrice = observer(({ open, onClose, factors, canIgnoreIncidents }) => {
  const classes = useStyles();
  const { offer: offerStore, quote: quoteStore } = useStore();
  const session = useSession();
  const toast = useToast();
  const offer = offerStore?.offer || quoteStore?.offer;

  // Underwriter ignores of auto claims and violations (#36871)
  const saveIgnores = useOfferIncidentIgnores();
  const [ignoreDialog, setIgnoreDialog] = useState(null);
  // each open gets a fresh dialog, so a retry after a failed save doesn't inherit the last one's checkboxes
  const dialogOpens = useRef(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // A save re-rates the offer, which remounts the People card (and sometimes this modal); it opens again
  // so the result is in view
  const [showPeopleAfterSave, setShowPeopleAfterSave] = useState(() => !!offerStore?.incidentIgnoreFollowUp);
  const [openPeopleSignal, setOpenPeopleSignal] = useState(0);

  const everyTieredViolationCounts = !!appConfig?.featureFlags?.countsEveryTieredAutoViolation;
  const incidentRows = useMemo(
    () =>
      buildAutoIncidentRows(offer?.quote?.drivers, {
        countsEveryTieredViolation: everyTieredViolationCounts,
        effectiveDate: offer?.quote?.global?.autoEffectiveDate,
        state: offerStore?.state
      }),
    [offer, everyTieredViolationCounts]
  );
  const selectedOption = offerStore?.selectedOption || '';
  const isBundleWithHome = selectedOption.includes('A') && /[HC]/.test(selectedOption);

  const peopleList = offer?.quote?.drivers?.length ? offer.quote.drivers : offer?.quote?.people;
  const findPerson = (key) => {
    const [firstName, lastName, dateOfBirth] = key.split('/');
    return peopleList?.find(
      (p) => p.firstName === firstName && p.lastName === lastName && p.dateOfBirth === dateOfBirth
    );
  };
  // every claim and violation on the dates the person's factors list, each with its status
  const personIncidents = (person, key) =>
    incidentRows.filter(
      (row) => row.driverId === person.id && factors.peopleFactors[key].some((factor) => factor.date === row.date)
    );
  const peopleItemCount = Object.keys(factors?.peopleFactors || {}).reduce((sum, key) => {
    const person = findPerson(key);
    const otherFactors = factors.peopleFactors[key].filter((factor) => !factor.date).length;
    return sum + (person ? otherFactors + personIncidents(person, key).length : 0);
  }, 0);

  const openIgnoreDialog = (mode, row) => {
    setSaveError(null);
    const others = getOtherIncidentsOnSameDates(incidentRows, [row], {
      stopIgnoring: mode === IgnoreDialogMode.StopIgnoring
    });
    setIgnoreDialog({
      key: ++dialogOpens.current,
      mode,
      incidents: [row],
      others,
      includeHome: true,
      homeOption:
        mode === IgnoreDialogMode.Ignore &&
        isBundleWithHome &&
        getHomeCopiesToIgnore(offer.quote, [row, ...others]).length > 0
    });
  };

  // The save re-rates the offer and the page reloads, so its outcome comes back through the store and is
  // picked up here, whether this modal stayed mounted or was opened again after the reload.
  const followUp = offerStore?.incidentIgnoreFollowUp;
  useEffect(() => {
    if (!followUp || followUp.status === 'saving') {
      return;
    }
    offerStore.setIncidentIgnoreFollowUp(null);
    setSaving(false);
    setShowPeopleAfterSave(true);
    setOpenPeopleSignal((signal) => signal + 1);
    if (followUp.status === 'failed') {
      const rowsById = new Map(incidentRows.map((row) => [row.id, row]));
      const rows = followUp.rowIds.map((id) => rowsById.get(id)).filter(Boolean);
      if (rows.length) {
        // the rows already include the same-date ones the underwriter kept checked
        setIgnoreDialog({
          key: ++dialogOpens.current,
          mode: followUp.mode,
          incidents: rows,
          others: [],
          homeOption: followUp.homeOption,
          includeHome: followUp.includeHome
        });
      }
      setSaveError(SAVE_ERROR);
      return;
    }
    setSaveError(null);
    setIgnoreDialog(null);
    toast.notify({ type: 'success', message: followUp.message });
  }, [followUp, offerStore, incidentRows]);

  const handleConfirmIgnore = async (rows, { includeHome }) => {
    const { mode } = ignoreDialog;
    const { quote } = offer;
    const homeIgnores = (quote.ignoredHomeIncidents || []).map(({ date, class: incidentClass }) => ({
      date,
      class: incidentClass
    }));
    let ignoredHomeIncidents;
    if (mode === IgnoreDialogMode.Ignore && includeHome) {
      ignoredHomeIncidents = [...homeIgnores, ...getHomeCopiesToIgnore(quote, rows)];
    } else if (mode === IgnoreDialogMode.StopIgnoring) {
      // stopping also lets any home copy of these claims count again
      const stoppedHomeKeys = new Set(
        rows.map(({ date, type }) => incidentKey({ date, class: homeClassForAutoClaimClass[type] }))
      );
      const keptHomeIgnores = homeIgnores.filter((ignored) => !stoppedHomeKeys.has(incidentKey(ignored)));
      ignoredHomeIncidents = keptHomeIgnores.length !== homeIgnores.length ? keptHomeIgnores : undefined;
    }

    setSaving(true);
    setSaveError(null);
    const followUpOnSave = {
      mode,
      rowIds: rows.map((row) => row.id),
      homeOption: ignoreDialog.homeOption,
      includeHome,
      message: savedMessage(mode, rows.length)
    };
    offerStore.setIncidentIgnoreFollowUp({ ...followUpOnSave, status: 'saving' });
    const saved = await saveIgnores({
      driverLists:
        mode === IgnoreDialogMode.Ignore
          ? getIgnoredIncidentsAfterIgnoring(quote.drivers, rows)
          : getIgnoredIncidentsAfterStopping([...(quote.drivers || []), ...(quote.removedDrivers || [])], rows),
      ignoredHomeIncidents
    });
    offerStore.setIncidentIgnoreFollowUp({ ...followUpOnSave, status: saved ? 'saved' : 'failed' });
    if (saved) {
      track('Staff Incidents Ignored', {
        offerId: offer.id,
        action: mode,
        incidentCount: rows.length,
        includesHome: !!includeHome
      });
    }
  };

  const { offerId: offerIdFromURL } = useParams();
  const { protocol, host } = window.location;
  const offerId = offerIdFromURL || offer?.id;
  const detailsLink = `${protocol}//${host}/offer/${offerId}/details`;

  useEffect(() => {
    if (open) {
      track('Reports and Pricing details viewed', { offerId });
    }
  }, [open, offerId]);

  const homePropertyFactors = factors?.homeFactors?.filter((f) => f.isPropertyFactor) ?? [];
  const homeClaims = factors?.homeFactors?.filter((f) => f.date) ?? [];

  const redirectToCompleteOfferDetails = () => {
    window.open(detailsLink, '_blank');
  };

  return (
    <BaseDialog open={open} onClose={onClose} blockContent size="md">
      <Label type="greenBig" className={classes.bottomSpacing}>
        Factors impacting price and eligibility
      </Label>
      <Label type="infoSubtitleSmall" className={classes.bottomSpacing}>
        These details come from reports and other information about this offer. Not all items affect price or
        eligibility, and it can vary by state.
      </Label>

      {/* ----- HOME FACTORS ----- */}
      {factors?.homeFactors?.length > 0 && (
        <CollapsibleCard
          content={<Label type="infoSubtitle">Home</Label>}
          contentRight={<span className={classes.factorCount}>{itemCountLabel(factors.homeFactors.length)}</span>}
        >
          <div className={classes.cardContent}>
            {homePropertyFactors.length > 0 && (
              <div className={classes.subsection}>
                <p className={classes.subsectionTitle}>Property details</p>
                <div className={classes.contentBox}>
                  {homePropertyFactors.map((factor, index) => (
                    <React.Fragment key={`home-property-${index}`}>
                      {index > 0 && <div className={classes.factorDivider} />}
                      <FactorItem factor={factor} classes={classes} />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {homeClaims.length > 0 && (
              <div className={classes.subsection}>
                <p className={classes.subsectionTitle}>Home claims</p>
                <div className={classes.contentBox}>
                  {homeClaims.map((factor, index) => (
                    <React.Fragment key={`home-claim-${index}`}>
                      {index > 0 && <div className={classes.factorDivider} />}
                      <FactorItem factor={factor} classes={classes} />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CollapsibleCard>
      )}

      {/* ----- PEOPLE FACTORS ----- */}
      {factors?.peopleFactors && Object.keys(factors.peopleFactors).length > 0 && (
        <CollapsibleCard
          initialOpen={showPeopleAfterSave}
          openSignal={openPeopleSignal}
          content={<Label type="infoSubtitle">People</Label>}
          contentRight={<span className={classes.factorCount}>{itemCountLabel(peopleItemCount)}</span>}
        >
          <div className={classes.cardContent}>
            {Object.keys(factors.peopleFactors).map((key) => {
              const person = findPerson(key);

              if (!person) {
                return null;
              }

              return (
                <div key={key} className={classes.subsection}>
                  <div className={classes.personSubsectionHeader}>
                    <p className={classes.subsectionTitle}>{`${person.firstName} ${person.lastName}`}</p>
                    {person.isPrimary && <span className={classes.roleLabel}>Applicant</span>}
                    {person.isCoApplicant && <span className={classes.roleLabel}>Co-applicant</span>}
                  </div>
                  <div className={classes.contentBox}>
                    {factors.peopleFactors[key]
                      .filter((factor) => !factor.date)
                      .map((factor, index) => (
                        <React.Fragment key={`person-factor-${index}`}>
                          {index > 0 && <div className={classes.factorDivider} />}
                          <FactorItem factor={factor} classes={classes} />
                        </React.Fragment>
                      ))}
                    <PersonIncidents
                      incidents={personIncidents(person, key)}
                      canIgnore={!!canIgnoreIncidents}
                      onIgnore={(row) => openIgnoreDialog(IgnoreDialogMode.Ignore, row)}
                      onStopIgnoring={(row) => openIgnoreDialog(IgnoreDialogMode.StopIgnoring, row)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleCard>
      )}

      {/* ----- CARS FACTORS ----- */}
      {factors?.carFactors && Object.keys(factors.carFactors).length > 0 && (
        <CollapsibleCard
          content={<Label type="infoSubtitle">Cars</Label>}
          contentRight={
            <span className={classes.factorCount}>
              {itemCountLabel(Object.values(factors.carFactors).reduce((sum, arr) => sum + arr.length, 0))}
            </span>
          }
        >
          <div className={classes.cardContent}>
            {Object.keys(factors.carFactors).map((VIN) => {
              const car = offer.quote.cars.find((c) => c.VIN === VIN);

              if (!car) {
                return null;
              }

              return (
                <div key={VIN} className={classes.subsection}>
                  <p className={classes.carSubsectionTitle}>{`${car.make} ${car.model} ${car.year}`}</p>
                  <div className={classes.contentBox}>
                    {factors.carFactors[VIN].map((factor, index) => (
                      <React.Fragment key={`car-factor-${index}`}>
                        {index > 0 && <div className={classes.factorDivider} />}
                        <FactorItem factor={factor} classes={classes} />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleCard>
      )}

      <Grid container justifyContent="flex-end" spacing={2} className={classes.buttons}>
        {session.canSeeFullOfferDetails && (
          <Grid item>
            <Button variant="contained" color="secondary" onClick={redirectToCompleteOfferDetails}>
              <img src={externalLinkIcon} alt="" className={classes.buttonIcon} />
              View complete offer details
            </Button>
          </Grid>
        )}
        <Grid item>
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        </Grid>
      </Grid>
      {ignoreDialog && (
        <IgnoreIncidentsDialog
          key={ignoreDialog.key}
          mode={ignoreDialog.mode}
          incidents={ignoreDialog.incidents}
          otherIncidentsOnDates={ignoreDialog.others}
          everyTieredViolationCounts={everyTieredViolationCounts}
          backLabel="Back to price and eligibility factors"
          homeOption={ignoreDialog.homeOption}
          defaultIncludeHome={ignoreDialog.includeHome}
          confirmLabel="Save and update price"
          loading={saving}
          error={saveError}
          onConfirm={handleConfirmIgnore}
          onClose={() => {
            setIgnoreDialog(null);
            setSaveError(null);
          }}
        />
      )}
    </BaseDialog>
  );
});

export default FactorsImpactingPrice;
